import './RouteModalWrapper.scss';

import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RouteModalWrapperProps } from 'types/AurigaRoutes.types';

/**
 * Wrapper for route content that should appear within a modal (e.g. Audit History)
 * - Use of this should be wrapped in a Route element, with modifications to AurigaRoutes.tsx file
 * @param title - Title of the modal
 * @param children - Content of to be rendered in the modal
 * @param parentRoute - Optional parent route to navigate to when closing (overrides default behavior)
 */
const RouteModalWrapper = ({ title, children, parentRoute }: RouteModalWrapperProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const background = location.state?.background;

  /**
   * Replaces route params with their values in any string with route params
   * - Used to replace params as needed (such as title, or parentRoute)
   * - Example: /:deckId -> /2
   * @param routePart - Route part to replace params in
   */
  const replaceParams = (routePart: string) => {
    return routePart.replace(/:[a-zA-Z]+/g, (match) => {
      const paramName = match.slice(1);
      return params[paramName] || match;
    });
  };

  // Replaces route params with their values in the title
  const titleWithParams = replaceParams(title);

  /**
   * Handle closing the modal
   * - If parentRoute is provided, navigate to it
   * - If there is a background location, navigate to it
   * - Otherwise go back one in history stack (i.e. the originating URL that invoked the modal)
   */
  const handleClose = () => {
    if (parentRoute) {
      // Replace route params in parentRoute with actual values
      const parentRouteWithParams = replaceParams(parentRoute);
      navigate(parentRouteWithParams, { replace: true });
    } else if (background?.pathname) {
      navigate(background.pathname, { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <Dialog open onClose={handleClose} className='modal-wrapper'>
      <DialogTitle className='modal-wrapper-title'>
        {titleWithParams}
        <IconButton onClick={handleClose} className='modal-wrapper-close'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
    </Dialog>
  );
};

export default RouteModalWrapper;
